import Highcharts from 'highcharts';
import highchartsMore from 'highcharts/highcharts-more';
import HighchartsReact from 'highcharts-react-official';
import { useTranslation } from 'react-i18next';

// Initialize the highcharts-more module for arearange chart type
const initHighchartsMore = highchartsMore as unknown as (hc: typeof Highcharts) => void;
if (typeof initHighchartsMore === 'function') {
    initHighchartsMore(Highcharts);
}
import ChartPlaceholder from './ChartPlaceholder';
import { createAdvancedChartBase } from './chartConfigUtils';

interface ForecastData {
    readonly period: string;
    readonly predictedContracts: number;
    readonly predictedRevenue: number;
    readonly confidenceLower: number;
    readonly confidenceUpper: number;
    readonly growthRate: number;
}

interface ForecastChartProps {
    readonly data: readonly ForecastData[];
    readonly loading?: boolean;
    readonly currency?: 'EUR' | 'LEI';
}

const numberAxisFormatter: Highcharts.AxisLabelsFormatterCallbackFunction = function () {
    return Highcharts.numberFormat(Number(this.value), 0, ',', ' ');
};

function createForecastTooltipFormatter(
    data: readonly ForecastData[],
    currencyLabel: string,
    t: (key: string, defaultValue: string) => string,
): Highcharts.TooltipFormatterCallbackFunction {
    return function () {
        const points = this.points;
        if (!points) return '';
        let tooltip = `<b>${this.x}</b><br/>`;
        const dataPoint = data.find(d => d.period === String(this.x));
        if (dataPoint) {
            tooltip += `${t('statistics.advanced.predicted', 'Predicted Revenue')}: <b>${Highcharts.numberFormat(dataPoint.predictedRevenue, 0, ',', ' ')} ${currencyLabel}</b><br/>`;
            tooltip += `${t('statistics.advanced.confidenceRange', 'Confidence Range')}: ${Highcharts.numberFormat(dataPoint.confidenceLower, 0, ',', ' ')} - ${Highcharts.numberFormat(dataPoint.confidenceUpper, 0, ',', ' ')} ${currencyLabel}<br/>`;
            tooltip += `${t('statistics.advanced.contracts', 'Contracts')}: <b>${dataPoint.predictedContracts}</b><br/>`;
            const growthColor = dataPoint.growthRate >= 0 ? '#52c41a' : '#ff4d4f';
            tooltip += `${t('statistics.advanced.growthRate', 'Growth')}: <span style="color:${growthColor}"><b>${dataPoint.growthRate >= 0 ? '+' : ''}${dataPoint.growthRate}%</b></span>`;
        }
        return tooltip;
    };
}

function ForecastChart({
    data,
    loading = false,
    currency = 'EUR',
}: ForecastChartProps): JSX.Element {
    const { t } = useTranslation();

    if (loading || data.length === 0) {
        return <ChartPlaceholder loading={loading} hasData={data.length > 0} />;
    }

    const currencyLabel = currency === 'LEI' ? 'RON' : 'EUR';

    const categories = data.map(d => d.period);
    const base = createAdvancedChartBase(
        'arearange',
        categories,
        t('statistics.advanced.period', 'Period'),
    );

    const options: Highcharts.Options = {
        ...base,
        yAxis: {
            title: {
                text: t('statistics.advanced.revenue', 'Revenue') + ` (${currencyLabel})`,
            },
            labels: {
                formatter: numberAxisFormatter,
            },
        },
        tooltip: {
            shared: true,
            formatter: createForecastTooltipFormatter(data, currencyLabel, t),
        },
        series: [
            {
                type: 'arearange',
                name: t('statistics.advanced.confidenceInterval', 'Confidence Interval'),
                data: data.map(d => [d.confidenceLower, d.confidenceUpper]),
                color: '#1890ff',
                fillOpacity: 0.2,
                lineWidth: 0,
                marker: {
                    enabled: false,
                },
            },
            {
                type: 'spline',
                name: t('statistics.advanced.predictedRevenue', 'Predicted Revenue'),
                data: data.map(d => d.predictedRevenue),
                color: '#1890ff',
                lineWidth: 3,
                marker: {
                    enabled: true,
                    radius: 5,
                    fillColor: '#1890ff',
                },
                zIndex: 1,
            },
            {
                type: 'column',
                name: t('statistics.advanced.predictedContracts', 'Predicted Contracts'),
                data: data.map(d => d.predictedContracts),
                color: '#52c41a',
                opacity: 0.6,
                yAxis: 0,
                visible: false, // Hidden by default, can be toggled
            },
        ],
        credits: {
            enabled: false,
        },
        legend: {
            enabled: true,
        },
    };

    return <HighchartsReact highcharts={Highcharts} options={options} />;
}

export default ForecastChart;
